import mixpanel from "mixpanel-browser";
import { getDtVite, getRuntimeEnv } from "@/lib/runtime-env";

/** Prefer K8s-injected runtime-env.js (prod); fall back to Vite .env for local dev */
function getMixpanelToken(): string | undefined {
  return getDtVite("DT_VITE_MIXPANEL_TOKEN");
}

/** Runtime (K8s) wins over Vite — matches `runtime-env.js` injection in dt-frontend Helm. */
function getMixpanelSessionReplayRaw(): string | undefined {
  const fromRuntime = getRuntimeEnv().DT_VITE_MIXPANEL_SESSION_REPLAY_ENABLED;
  if (fromRuntime !== undefined) return fromRuntime;
  return import.meta.env.DT_VITE_MIXPANEL_SESSION_REPLAY_ENABLED;
}

/** Unset = replay on (.env.example). false / 0 / off / no = off. */
function getMixpanelRecordSessionsPercent(): number {
  const raw = getMixpanelSessionReplayRaw();
  if (raw === undefined || raw === "") {
    return import.meta.env.PROD ? 100 : 1;
  }
  if (["false", "0", "off", "no"].includes(String(raw).toLowerCase().trim())) {
    return 0;
  }
  return import.meta.env.PROD ? 100 : 1;
}

let isMixpanelInitialized = false;

/**
 * Initialize all analytics providers.
 * Safe to call multiple times - each provider initializes at most once.
 * Providers without tokens are silently skipped.
 */
export function initAnalytics(): void {
  if (typeof window === "undefined") return;

  const mixpanelToken = getMixpanelToken();
  if (!isMixpanelInitialized && mixpanelToken) {
    mixpanel.init(mixpanelToken, {
      record_sessions_percent: getMixpanelRecordSessionsPercent(),
      record_mask_text_selector: "",
      persistence: "localStorage",
    });
    isMixpanelInitialized = true;
  }
}

export interface AnalyticsUserLike {
  id: string;
  email?: string;
  name?: string;
  isPlatformAdmin?: boolean;
  emailVerified?: boolean;
  organizationToken?: string;
  orgRole?: string;
  subscriptionTier?: string;
  pendingTier?: string | null;
  trialEndsAt?: string | null;
  lsStatus?: string | null;
}

/**
 * Sync Mixpanel identity with auth state. Call after login/profile fetch and on logout.
 */
export function syncAnalyticsUser(user: AnalyticsUserLike | null): void {
  if (!isMixpanelInitialized) return;
  if (!user?.id) {
    mixpanel.reset();
    return;
  }
  mixpanel.identify(user.id);
  // PII is allowed only on Mixpanel People/User Profile for lookup and
  // filtering; raw email/name should not be copied onto normal events.
  const peopleProps: Record<string, any> = {
    ...(user.email ? { $email: user.email, email_domain: user.email.split('@')[1] } : {}),
    ...(user.name ? { $name: user.name } : {}),
    is_email_verified: user.emailVerified ?? false,
    is_platform_admin: user.isPlatformAdmin ?? false,
    is_internal_user: user.email?.endsWith('@fairgen.ai') ?? false,
    organization_id: user.organizationToken,
    organization_role: user.orgRole,
    plan_tier: user.subscriptionTier,
    pending_tier: user.pendingTier,
    subscription_status: user.lsStatus ?? (user.subscriptionTier === "trial" ? "trial" : undefined),
    trial_ends_at: user.trialEndsAt,
  };
  Object.keys(peopleProps).forEach(
    (key) => peopleProps[key] === undefined && delete peopleProps[key],
  );
  mixpanel.people.set(peopleProps);
  if (user.organizationToken) {
    mixpanel.set_group("organization_id", user.organizationToken);
  }
}

/**
 * Identify a user (call after login/registration)
 */
export function identify(userId: string, traits?: Record<string, any>): void {
  if (isMixpanelInitialized) {
    mixpanel.identify(userId);
    if (traits) {
      mixpanel.people.set(traits);
    }
  }
}

let _lastEvent: { event: string; props: Record<string, string> } | null = null;

function stripToIdProps(
  raw?: Record<string, any>,
): Record<string, string> {
  if (!raw) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw)) {
    // `_lastEvent` is debug/test-only. Keep `session_status` because import
    // session assertions predate the canonical `_id`-only snapshot convention.
    if (k.endsWith('_id') || k === 'session_status') {
      out[k] = String(v);
    }
  }
  return out;
}

export function getLastAnalyticsEvent() {
  return _lastEvent;
}

/**
 * Track a custom event
 */
export function track(event: string, properties?: Record<string, any>): void {
  _lastEvent = { event, props: stripToIdProps(properties) };

  if (isMixpanelInitialized) {
    mixpanel.track(event, properties);
  }
}

/**
 * Track a page view (useful for SPA navigation)
 */
export function trackPageView(pageName?: string): void {
  const page = pageName || window.location.pathname;

  if (isMixpanelInitialized) {
    mixpanel.track_pageview({ page });
  }
}

/**
 * Reset analytics (call on logout)
 */
export function reset(): void {
  if (isMixpanelInitialized) {
    mixpanel.reset();
  }
}

/**
 * Set user properties without tracking an event
 */
export function setUserProperties(properties: Record<string, any>): void {
  if (isMixpanelInitialized) {
    mixpanel.people.set(properties);
  }
}

/**
 * Check if any analytics provider is enabled
 */
export function isAnalyticsEnabled(): boolean {
  return isMixpanelInitialized;
}

export { mixpanel };

// ============================================================================
// PLG + product event constants (snake_case). Backend: see
// apps/backend/src/mixpanel/types/mixpanel-event.enum.ts (SERVER_EMITTED_*).
// ============================================================================

export const AnalyticsEvents = {
  // Acquisition
  SIGNUP_STARTED: "signup_started",
  USER_REGISTERED: "user_registered",
  EMAIL_VERIFIED: "email_verified",
  USER_LOGGED_IN: "user_logged_in",
  USER_LOGGED_OUT: "user_logged_out",

  // Activation (Aha Moments)
  ONBOARDING_STARTED: "onboarding_started",
  ONBOARDING_STEP_COMPLETED: "onboarding_step_completed",
  ONBOARDING_COMPLETED: "onboarding_completed",
  ONBOARDING_ABANDONED: "onboarding_abandoned",
  FIRST_EXPERIMENT_CREATED: "first_experiment_created",
  FIRST_EXPERIMENT_COMPLETED: "first_experiment_completed",

  // Engagement — studies / experiments
  STUDY_CREATED: "study_created",
  STUDY_STARTED: "study_started",
  STUDY_COMPLETED: "study_completed",
  EXPERIMENT_CREATED: "experiment_created",
  EXPERIMENT_COMPLETED: "experiment_completed",
  EXPERIMENT_CANCELLED: "experiment_cancelled",
  RESULTS_VIEWED: "results_viewed",
  REPORT_SHARED: "report_shared",
  REPORT_CHAT_USED: "report_chat_used",
  TWIN_CHAT_STARTED: "twin_chat_started",
  SURVEY_CREATED: "survey_created",
  POPULATION_UPLOADED: "population_uploaded",

  // Monetization / PLS Signals
  CHECKOUT_STARTED: "checkout_started",
  TRIAL_STARTED: "trial_started",
  SUBSCRIPTION_STARTED: "subscription_started",
  INVOICE_PAID: "invoice_paid",
  INVOICE_FAILED: "invoice_failed",
  SUBSCRIPTION_CANCELLED: "subscription_cancelled",
  SUBSCRIPTION_EXPIRED: "subscription_expired",
  CREDITS_USED: "credits_used",
  CREDITS_LOW: "credits_low",
  CREDITS_EXHAUSTED: "credits_exhausted",

  // Audience — import
  AUDIENCE_IMPORT_SESSION_STARTED: "audience_import_session_started",
  AUDIENCE_IMPORT_WORKBOOK_SHEETS_ASSIGNED: "audience_import_workbook_sheets_assigned",
  AUDIENCE_IMPORT_AUDIENCE_CREATION_REQUESTED: "audience_import_audience_creation_requested",
  AUDIENCE_IMPORT_SESSION_DISCARDED: "audience_import_session_discarded",

  // Audience — detail & management
  AUDIENCE_DETAIL_VIEWED: "audience_detail_viewed",
  /** Fires only when the viewer is the creator AND it is their first ever view of this audience. Used to trigger the post-setup feedback survey. */
  AUDIENCE_FIRST_CREATOR_VIEW: "audience_first_creator_view",
  AUDIENCE_NARRATIVE_SAVED: "audience_narrative_saved",
  AUDIENCE_SHARING_MODE_UPDATED: "audience_sharing_mode_updated",
  AUDIENCE_COLLABORATOR_ADDED: "audience_collaborator_added",
  AUDIENCE_COLLABORATOR_REMOVED: "audience_collaborator_removed",
  AUDIENCE_CONTEXT_FILE_UPLOADED: "audience_context_file_uploaded",
  AUDIENCE_CONTEXT_FILE_REMOVED: "audience_context_file_removed",

  // Study wizard + chat session
  STUDY_CREATION_OPENED: "study_creation_opened",
  STUDY_CREATION_STEP_VIEWED: "study_creation_step_viewed",
  STUDY_WIZARD_OPENED: "study_wizard_opened",
  STUDY_WIZARD_STEP_VIEWED: "study_wizard_step_viewed",
  STUDY_TYPE_CONFIRMED: "study_type_confirmed",
  STUDY_AUDIENCE_SELECTED: "study_audience_selected",
  STUDY_CHAT_SESSION_CREATED: "study_chat_session_created",
  STUDY_CHAT_SESSION_RESUMED: "study_chat_session_resumed",
  STUDY_CHAT_MESSAGE_SENT: "study_chat_message_sent",
  STUDY_CHAT_UNDO: "study_chat_undo",
  STUDY_CREATION_FAILED: "study_creation_failed",
  HOME_STUDY_FLOW_STARTED: "home_study_flow_started",
  HOME_ROOM_QUICKSTART_CREATED: "home_room_quickstart_created",
  COMPOSE_MODAL_OPENED: "compose_modal_opened",
  ROOM_CREATED: "room_created",
  CHAT_ACTIVITY: "chat_activity",
  FIRST_MESSAGE_SENT: "first_message_sent",

  // Studies list & lifecycle
  STUDIES_LIST_VIEWED: "studies_list_viewed",
  EXPERIMENT_DELETED: "experiment_deleted",

  // Results / report / deck / sharing
  RESULTS_PAGE_VIEWED: "results_page_viewed",
  EXPERIMENT_REPORT_REGENERATED: "experiment_report_regenerated",
  EXPERIMENT_RERUN: "experiment_rerun",
  DECK_AVAILABLE_VIEWED: "deck_available_viewed",
  EXPERIMENT_REPORT_CHAT_MESSAGE_SENT: "experiment_report_chat_message_sent",
  EXPERIMENT_TWIN_CHAT_MESSAGE_SENT: "experiment_twin_chat_message_sent",
  REPORT_ANALYST_TAB_OPENED: "report_analyst_tab_opened",
  REPORT_ANALYST_MESSAGE_SENT: "report_analyst_message_sent",
  REPORT_ANALYST_CUSTOM_ANALYSIS_PENDING: "report_analyst_custom_analysis_pending",
  REPORT_ANALYST_CUSTOM_ANALYSIS_COMPLETED: "report_analyst_custom_analysis_completed",
  REPORT_ANALYST_RESET: "report_analyst_reset",
  REPORT_ANALYST_ERROR: "report_analyst_error",
  SHARE_STUDY_MODAL_OPENED: "share_study_modal_opened",
  EXPERIMENT_SHARING_SAVED: "experiment_sharing_saved",
  STUDY_SHARE_LINK_COPIED: "study_share_link_copied",

  // Billing — trial → audience upgrade modal
  TRIAL_AUDIENCE_UPGRADE_MODAL_VIEWED: "trial_audience_upgrade_modal_viewed",
  TRIAL_AUDIENCE_UPGRADE_CHECKOUT_STARTED: "trial_audience_upgrade_checkout_started",
  TRIAL_AUDIENCE_UPGRADE_PLANS_CLICKED: "trial_audience_upgrade_plans_clicked",
  TRIAL_AUDIENCE_UPGRADE_DISMISSED: "trial_audience_upgrade_dismissed",

  // Billing — trial operational limits (messages / rooms / studies)
  TRIAL_LIMIT_REACHED: "trial_limit_reached",
  TRIAL_LIMIT_UPGRADE_CHECKOUT_STARTED: "trial_limit_upgrade_checkout_started",
  TRIAL_LIMIT_UPGRADE_PLANS_CLICKED: "trial_limit_upgrade_plans_clicked",
  TRIAL_LIMIT_UPGRADE_DISMISSED: "trial_limit_upgrade_dismissed",

  // Brand settings
  BRAND_SETTINGS_SAVED: "brand_settings_saved",
  BRAND_SETTINGS_SAVE_FAILED: "brand_settings_save_failed",

  // Support
  SUPPORT_SIDEBAR_CLICKED: "support_sidebar_clicked",

  // Feedback
  FEEDBACK_SHOWN: "feedback_shown",
  FEEDBACK_DISMISSED: "feedback_dismissed",
  FEEDBACK_SCORE_SELECTED: "feedback_score_selected",
  FEEDBACK_SUBMITTED: "feedback_submitted",
  FEEDBACK_AUTO_TRIGGERED: "feedback_auto_triggered",
  FEEDBACK_AUTO_SUPPRESSED: "feedback_auto_suppressed",

  // Referral + bonus credits
  REFERRAL_LINK_COPIED: "referral_link_copied",
  REFERRAL_LINK_CLICKED: "referral_link_clicked",
  REFERRAL_COMPLETED: "referral_completed",
  PROMO_CODE_APPLIED: "promo_code_applied",

  // Onboarding personalization
  ONBOARDING_JTBD_SELECTED: "onboarding_jtbd_selected",
  ONBOARDING_INDUSTRY_SELECTED: "onboarding_industry_selected",
  ONBOARDING_AUDIENCES_BOOKMARKED: "onboarding_audiences_bookmarked",
  ONBOARDING_AUDIENCE_DISMISSED: "onboarding_audience_dismissed",

  // Quick Study — credit billing
  /** A Quick Study sample question completed and was charged (server-confirmed on success). */
  QUICK_STUDY_QUESTION_CHARGED: "quick_study_question_charged",
  /** A running Quick Study question was cancelled mid-run and charged the cancel fee. */
  QUICK_STUDY_RUN_CANCELLED: "quick_study_run_cancelled",

  // Quick Study — combo quant+OE ("ask with follow-up why")
  /** The marketer removed the agent-proposed paired follow-up (the ✕ on the confirm card's
   *  follow-up row, `paired_oe` set to '' — explicit OFF). Feeds the combo default-on plan's
   *  D3 escape hatch: watching removal rate per question shape to catch a pairing kind that
   *  reads wrong for a given instrument before it erodes trust in the default-on follow-up. */
  QUICK_STUDY_FOLLOWUP_REMOVED: "quick_study_followup_removed",

  // Study + chat sharing (guest funnel)
  STUDY_SHARED: "study_shared",
  SHARED_STUDY_VIEWED: "shared_study_viewed",
  CHAT_SHARED: "chat_shared",
  CHAT_SHARE_VIEWED: "chat_share_viewed",
  GUEST_ASK_ABOUT_CLICKED: "guest_ask_about_clicked",
  GUEST_ASK_ABOUT_REPLY_RECEIVED: "guest_ask_about_reply_received",

  // Questionnaire topic-coverage signal (RND-2597)
  COVERAGE_ASSESSMENT_COMPUTED: "coverage_assessment_computed",
  COVERAGE_CHIP_VIEWED: "coverage_chip_viewed",
  COVERAGE_FLAGGED_QUESTION_RESOLVED: "coverage_flagged_question_resolved",

  // Soft-validation gate: issue chips and unmapped/turn-failure telemetry
  QUESTIONNAIRE_ISSUE_CHIP_VIEWED: "questionnaire_issue_chip_viewed",
  QUESTIONNAIRE_ISSUE_UNMAPPED: "questionnaire_issue_unmapped",
  QUESTIONNAIRE_TURN_FAILURE: "questionnaire_turn_failure",

  // Twin chat — client crash / history restore (RND-2798). Client-emitted only.
  // room_id, study_id, crash_source (react | window | unhandledrejection | history_restore), send_started.
  CHAT_PAGE_CRASH: "chat_page_crash",
} as const;

export type AnalyticsEvent =
  (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];
